import { DirectoryTenant } from '../../components/services/userDirectoryService';

/** One row of TenantAPI's `GET tenantservice/tenant/dashboard/tenants`. */
export interface Tenant {
  tenantId: string;
  tenantName: string;
  websiteURL: string;
  logoURL: string | null;
  onboardingDate: string;
  defatomUserSignUpID: string | null;
  customerType: string;
  onboardedBy: string;
  isActive: boolean;
  isGlobalTenant: boolean;
  deleted: boolean;
  isTestTenant: boolean | null;
  status: string;
  provisioningError: string | null;
  totalLicenses: number;
  licensesCreatedDate: string;
  licensesActivateToDate: string;
  isLicenseActive: boolean;
  compliantAgentCount: number;
  nonCompliantAgentCount: number;
  totalAgentCount: number;
}

export interface ResellerGroup {
  reseller: Tenant;
  tenants: Tenant[];
}

export interface TenantGrouping {
  resellers: ResellerGroup[];
  // Tenants no reseller manages.
  direct: Tenant[];
}

const idOf = (id: string) => String(id ?? '').toLowerCase();
const byName = (a: Tenant, b: Tenant) => a.tenantName.localeCompare(b.tenantName, undefined, { sensitivity: 'base' });

// Same match as the page's search has always used: any field containing the text.
export const tenantMatchesSearch = (tenant: Tenant, search: string): boolean => {
  const term = search.trim().toLowerCase();
  return !term || Object.values(tenant).some((value) => String(value).toLowerCase().includes(term));
};

// Reseller id -> its customer tenants' ids, from every Reseller node in the tree.
const resellerMapping = (tree: DirectoryTenant | null): Map<string, Set<string>> => {
  const mapping = new Map<string, Set<string>>();
  const walk = (node: DirectoryTenant) => {
    if (node.tenantType === 'Reseller') {
      const children = mapping.get(node.tenantId) ?? new Set<string>();
      node.tenants.forEach((child) => children.add(child.tenantId));
      mapping.set(node.tenantId, children);
    }
    node.tenants.forEach(walk);
  };
  if (tree) walk(tree);
  return mapping;
};

/**
 * Groups the tenant list by reseller, using AuthService's directory tree for "which tenants belong to which
 * reseller" (TenantAPI's list has no such link). A reseller is a tenant the tree marks as Reseller, or whose
 * `customerType` is Reseller (shown with no tenants if the tree doesn't list it). A tenant mapped to two resellers
 * shows under both. Tenants under no reseller stay in `direct`, in the list's order; resellers and their tenants
 * are sorted by name. Tree entries missing from the list (e.g. deleted tenants) are ignored.
 */
export const groupTenantsByReseller = (tenants: Tenant[], tree: DirectoryTenant | null): TenantGrouping => {
  const mapping = resellerMapping(tree);
  const isReseller = (t: Tenant) => mapping.has(idOf(t.tenantId)) || t.customerType?.toLowerCase() === 'reseller';

  const resellers = tenants.filter(isReseller);
  const managed = new Set<string>();
  const groups = resellers
    .map((reseller) => {
      const childIds = mapping.get(idOf(reseller.tenantId)) ?? new Set<string>();
      const children = tenants.filter((t) => childIds.has(idOf(t.tenantId)) && !isReseller(t));
      children.forEach((t) => managed.add(idOf(t.tenantId)));
      return { reseller, tenants: [...children].sort(byName) };
    })
    .sort((a, b) => byName(a.reseller, b.reseller));

  return {
    resellers: groups,
    direct: tenants.filter((t) => !isReseller(t) && !managed.has(idOf(t.tenantId))),
  };
};

/**
 * Applies the search to a grouping: a reseller whose own record matches keeps all its tenants; otherwise it is
 * kept with only its matching tenants, or dropped when none match.
 */
export const searchTenantGrouping = (grouping: TenantGrouping, search: string): TenantGrouping => {
  if (!search.trim()) return grouping;
  return {
    resellers: grouping.resellers
      .map((group) =>
        tenantMatchesSearch(group.reseller, search)
          ? group
          : { ...group, tenants: group.tenants.filter((t) => tenantMatchesSearch(t, search)) },
      )
      .filter((group) => tenantMatchesSearch(group.reseller, search) || group.tenants.length > 0),
    direct: grouping.direct.filter((t) => tenantMatchesSearch(t, search)),
  };
};
